import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type LogoVariant =
  | 'black'
  | 'white'
  | 'full'
  | 'full-black'
  | 'full-white'
  | 'full-orange'
  | 'white-isotype'
  | 'isotype';

type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

@Component({
  selector: 'stx-ui-logo',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage],
  host: {
    class: 'inline-block',
    '[style.width]': "width() + 'px'",
    '[style.height]': "height() + 'px'",
  },
  // The theme swap cannot be written with the global `dark:` variant: that
  // stylesheet is applied after the browser has already requested these images,
  // so only a rule inlined alongside the component decides which file loads.
  // `:host-context` is the one selector that reaches an ancestor of the host.
  styles: `
    .stx-logo-dark {
      display: none;
    }

    :host-context([data-theme='dark']) .stx-logo-light {
      display: none;
    }

    :host-context([data-theme='dark']) .stx-logo-dark {
      display: revert-layer;
    }
  `,
  template: `
    @if (themeAware()) {
      <!-- Both variants ship so the pre-paint theme attribute can pick one.
           loading="lazy" is load-bearing: a lazy image with no layout box is
           never fetched, which is what keeps the hidden variant off the wire.
           Making these eager would download both files. -->
      <img
        alt="Seatix Logo"
        class="stx-logo-light"
        loading="lazy"
        [height]="height()"
        [width]="width()"
        [ngSrc]="lightSrc()"
      />
      <img
        alt="Seatix Logo"
        class="stx-logo-dark"
        loading="lazy"
        [height]="height()"
        [width]="width()"
        [ngSrc]="darkSrc()"
      />
    } @else {
      <img
        alt="Seatix Logo"
        loading="lazy"
        [height]="height()"
        [width]="width()"
        [ngSrc]="src()"
      />
    }
  `,
})
export class LogoComponent {
  readonly size = input<LogoSize>('md');
  readonly variant = input<LogoVariant>();

  #baseHeight = 25;
  readonly #baseWidth = computed(() =>
    this.variant() === 'isotype' || this.variant() === 'white-isotype'
      ? 25
      : 124,
  );
  readonly #multiplier = computed(() => {
    switch (this.size()) {
      case 'xs':
        return 0.75;
      case 'sm':
        return 1;
      case 'md':
        return 1.5;
      case 'lg':
        return 2;
      case 'xl':
        return 4;
      case '2xl':
        return 6;
      case '3xl':
        return 8;
      case '4xl':
        return 10;
    }
  });
  readonly height = computed(() => {
    return this.#multiplier() * this.#baseHeight;
  });
  readonly width = computed(() => {
    return this.#multiplier() * this.#baseWidth();
  });
  #path = '/assets/img/logo';

  // A theme-following logo needs both files in the markup, because the server
  // has no way to know the visitor's theme. A named variant is a deliberate
  // choice and stays a single file.
  readonly themeAware = computed(() => {
    const variant = this.variant();
    return !variant || variant === 'full';
  });

  readonly lightSrc = computed(() => this.#themeSrc('black'));
  readonly darkSrc = computed(() => this.#themeSrc('white'));

  #themeSrc(suffix: 'black' | 'white'): string {
    const infix = this.variant() === 'full' ? 'full-' : '';
    return `${this.#path}/seatix-${infix}${suffix}.png`;
  }

  // The one file this render shows. A named variant has its own file; a
  // theme-following one has no single file, so this answers with the variant a
  // server without a theme would render.
  readonly src = computed(() =>
    this.themeAware()
      ? this.lightSrc()
      : `${this.#path}/seatix-${this.variant()}.png`,
  );
}
